<template>
    <div class="report-content">
        <div class="detailing">
            <Hide v-model="table1Visibility">
                <h4>{{ $t('reports.exceptionReport.timeStatistics') }}</h4>
            </Hide>
        </div>
        <div v-show="table1Visibility">
            <table v-if="!table1Loader && timeStatistics.length > 0" class="common-table time-statistics">
                <tr>
                    <th>{{ $t('common.location') }}</th>
                    <th>{{ $t('reports.exceptionReport.activityCount') }}</th>
                    <th>{{ $t('reports.exceptionReport.averageTime') }}</th>
                    <th>{{ $t('reports.exceptionReport.maxTime') }}</th>
                    <th>{{ $t('reports.exceptionReport.withinTarget') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(loc, index) in timeStatistics" :key="index">
                        <td class="location">{{ loc.location }}</td>
                        <td>{{ loc.activity_count }}</td>
                        <td>{{ loc.avgTimeFormatted }}</td>
                        <td>{{ loc.maxTimeFormatted }}</td>
                        <td>{{ percent(loc.within_target, loc.activity_count) }}%</td>
                    </tr>
                    <tr>
                        <td>{{ $t('common.total') }}</td>
                        <td>{{ timeStatisticsTotal.activity_count }}</td>
                        <td>{{ timeStatisticsTotal.avg_time }}</td>
                        <td>{{ timeStatisticsTotal.max_time }}</td>
                        <td>{{ timeStatisticsTotal.within_target }}</td>
                    </tr>
                </tbody>
            </table>

            <LoadingBlock v-if="table1Loader" />
            <h4 v-if="!table1Loader && timeStatistics.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>

        <div class="graphics exception-report">
            <Hide v-model="chartVisibility">
                <h4
                    :data-tooltip="$t('reports.exceptionReport.more26')"
                    :class="[{ 'with-tooltip': moreThan26Weeks }]"
                >
                    {{ $t('reports.exceptionReport.weekRespTime') }} <span v-if="moreThan26Weeks"> *</span>
                </h4>
            </Hide>
            <div v-show="chartVisibility">
                <div v-show="!chartLoader && weekPerformance.length > 0">
                    <div class="exception-report-chart"></div>
                </div>
                <LoadingBlock v-if="chartLoader" />
                <h4 v-if="!chartLoader && weekPerformance.length === 0" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>
        </div>

        <div class="detailed-exception-tables">
            <div class="detailing">
                <Hide v-model="table2Visibility">
                    <h4>{{ $t('reports.exceptionReport.detailedTable1') }}</h4>
                </Hide>
            </div>
            <div v-show="table2Visibility">
                <Pagination
                    v-if="exceptionLength > 5"
                    v-model="pagination.current_page"
                    :options="pagination"
                    :callback="handlePaginationCallback"
                    :to-cache-pages="toCachePages"
                />
                <table v-if="!table2Loader && reportData.length > 0" class="common-table">
                    <tr>
                        <th>{{ $t('common.location') }}</th>
                        <th>{{ $t('reports.exceptionReport.responseTime') }}</th>
                        <th>{{ $t('reporting.commonLabels.startTimeDate') }}</th>
                        <th>{{ $t('reporting.commonLabels.endTimeDate') }}</th>
                    </tr>
                    <tbody>
                        <tr v-for="(loc, index) in reportData" :key="index">
                            <td class="location">{{ loc.location }}</td>
                            <td :style="{ color: timeColor(loc.actual_call_time) }">
                                {{ loc.actualCallTimeFormatted }}
                            </td>
                            <td>{{ loc.startTimeFormatted }}</td>
                            <td>{{ loc.endTimeFormatted }}</td>
                        </tr>
                    </tbody>
                </table>
                <LoadingBlock v-if="table2Loader" />
                <Pagination
                    v-if="exceptionLength > 5"
                    v-model="pagination.current_page"
                    :options="pagination"
                    :callback="handlePaginationCallback"
                    :to-cache-pages="toCachePages"
                />

                <h4 v-if="!table2Loader && reportData.length === 0" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>

            <div class="detailing">
                <Hide v-model="table3Visibility">
                    <h4>{{ $t('reports.exceptionReport.detailedTable2') }}</h4>
                </Hide>
            </div>
            <div v-show="table3Visibility">
                <Pagination
                    v-if="exceptionLength > 5"
                    v-model="pagination.current_page"
                    :options="pagination"
                    :callback="handlePaginationCallback"
                    :to-cache-pages="toCachePages"
                />
                <table v-if="!table2Loader && reportData.length > 0" class="common-table">
                    <tr>
                        <th>{{ $t('common.location') }}</th>
                        <th>{{ $t('reports.exceptionReport.responseTime') }}</th>
                        <th>{{ $t('reporting.commonLabels.startTimeDate') }}</th>
                        <th>{{ $t('reports.reportForm.events') }}</th>
                    </tr>
                    <tbody>
                        <tr v-for="(loc, index) in reportData" :key="index">
                            <td class="location">{{ loc.location }}</td>
                            <td :style="{ color: timeColor(loc.actual_call_time) }">
                                {{ loc.actualCallTimeFormatted }}
                            </td>
                            <td>
                                <p v-for="(event, index2) in loc.details" :key="index2">
                                    {{ event.timeFormatted }}
                                </p>
                            </td>
                            <td>
                                <p v-for="(event, index2) in loc.details" :key="index2">
                                    <img
                                        v-if="showColoredIcon(event.icons[0])"
                                        :src="'static/img/' + iconClass(event.icons[0]) + '.svg'"
                                        alt=""
                                    />
                                    <i
                                        v-else
                                        :class="[
                                            'icon-' + iconClass(event.icons[0]),
                                            { canceled: event.icons[1] },
                                        ]"
                                    ></i>
                                    {{ $t('reporting.detail.' + event.key, event.params) }}
                                </p>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <LoadingBlock v-if="table2Loader" />
                <Pagination
                    v-if="exceptionLength > 5"
                    v-model="pagination.current_page"
                    :options="pagination"
                    :callback="handlePaginationCallback"
                    :to-cache-pages="toCachePages"
                />

                <h4 v-if="!table2Loader && reportData.length === 0" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import isEqual from 'lodash-es/isEqual';
import Chartist from 'ascom-chartist-logscale';
import Hide from '../../common/Hide.vue';
import {
    verticalChartSeries,
    exceptionChartConfiguration,
    showColoredIcon,
    iconClass,
    flipThePage,
    toCachePages,
} from '../helpers/reportsHelper';
import {
    resourceExceptionTimeReport,
    resourceExceptionTimeStatistics,
    resourceExceptionWeekPerformance,
    resourceExceptionLength,
} from '../../../api/resources';
import { getCancelToken } from '../../../api';
import { formatDateString, secsToReadableTime } from '../../common/helpers';
import { mapState, mapActions } from 'vuex';
import LoadingBlock from '../../common/LoadingBlock';
import Pagination from '../../common/Pagination.vue';
import { ERROR } from '../../../constants';

export default {
    components: {
        Pagination,
        Hide,
        LoadingBlock,
    },

    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            weekPerformance: [],
            timeStatistics: [],
            reportData: [],
            exceptionLength: 0,
            cache: {},
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: 5,
            },
            moreThan26Weeks: false,
            table1Loader: true,
            table2Loader: true,
            chartLoader: true,
            chartVisibility: true,
            table1Visibility: true,
            table2Visibility: true,
            table3Visibility: true,
            cancelToken: getCancelToken(),
            cancelPaginationToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({ listCallTypes: 'callTypes' }),

        /**
         * Calculations for last column in the Time statistics table
         * @returns {Object}
         */
        timeStatisticsTotal() {
            let total = this.timeStatistics.reduce(
                (c, v) => {
                    c.activity_count += v.activity_count;
                    c.avg_time += v.avg_time;
                    c.max_time = v.max_time > c.max_time ? v.max_time : c.max_time;
                    c.within_target += v.within_target;
                    return c;
                },
                {
                    activity_count: 0,
                    avg_time: 0,
                    max_time: 0,
                    within_target: 0,
                }
            );

            return {
                ...total,
                avg_time: secsToReadableTime(total.avg_time / this.timeStatistics.length),
                max_time: secsToReadableTime(total.max_time),
                within_target: Math.floor((total.within_target / total.activity_count) * 100) + '%',
            };
        },

        callTypesSets() {
            return this.listCallTypes.find(ct => ct.id === Number(this.reportParameters.call_type));
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
                this.loadReport();
            }
        },

        chartVisibility(value) {
            if (value) {
                this.chartBuilder(this.weekPerformance);
            }
        },
    },

    mounted() {
        this.loadReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        this.cancelPaginationToken.cancel();
    },

    methods: {
        showColoredIcon,
        iconClass,
        toCachePages,

        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * Load report data. In case of error will be displayed popup message with text.
         */
        async loadReport() {
            try {
                await Promise.all([
                    this.loadTimeStatistics(),
                    this.loadWeekPerformance(),
                    this.loadExceptionLengthAndTableData(),
                ]);
            } catch (error) {
                this.table1Loader = false;
                this.table2Loader = false;
                this.chartLoader = false;
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            }
        },

        /**
         * Getting count of records in the table. If value > 0 then will be initialized loadTableData function.
         */
        async loadExceptionLengthAndTableData() {
            this.exceptionLength = (
                await resourceExceptionLength({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            if (this.exceptionLength) {
                this.pagination.last_page = Math.ceil(this.exceptionLength / this.pagination.per_page);
                this.loadTableData();
            } else {
                this.table2Loader = false;
            }
        },

        /**
         * Loading data for Time statistics table.
         */
        async loadTimeStatistics() {
            this.timeStatistics = (
                await resourceExceptionTimeStatistics({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data.map(loc => ({
                ...loc,
                avgTimeFormatted: secsToReadableTime(loc.avg_time),
                maxTimeFormatted: secsToReadableTime(loc.max_time),
            }));
            this.table1Loader = false;
        },

        /**
         * Loading data for Week performance chart. Initializing chartBuilder function if data is not null
         * for building chart.
         */
        async loadWeekPerformance() {
            this.weekPerformance = (
                await resourceExceptionWeekPerformance({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            this.chartLoader = false;
            if (this.weekPerformance.length > 0) {
                this.chartBuilder(this.weekPerformance);
            }
        },

        /**
         * Set series and options for chart.
         * @param {[]} data
         */
        chartBuilder(data) {
            let labels = data.map(el => 'w' + el.week);
            const meta = [
                this.$t('reports.exceptionReport.withinTarget'),
                this.$t('reports.exceptionReport.delayed'),
                this.$t('reports.exceptionReport.late'),
            ];

            let labelsLength = labels.length;
            if (labelsLength > 26) {
                this.moreThan26Weeks = true;
                labels = labels.slice(-26);
            }

            let series = verticalChartSeries(data, meta, this.moreThan26Weeks);
            let chartData = { labels, series };

            let w = labelsLength > 15 ? 4 : labelsLength > 10 ? 8 : labelsLength > 5 ? 12 : 20;

            let options = exceptionChartConfiguration(w, meta);

            new Chartist.Bar('.exception-report-chart', chartData, options).on('draw', data => {
                if (data.type === 'bar') {
                    data.element.attr({
                        style: 'stroke-width: ' + w + 'px',
                    });
                }
            });
        },

        handlePaginationCallback() {
            this.table2Loader = false;
            flipThePage.call(this);
        },

        /**
         * Loading table data depends on pagination parameters. By default, the data is loaded from the 0 page.
         * @return {Promise<void>}
         */
        async loadTableData() {
            this.table2Loader = true;
            let data = {
                ...this.reportParameters,
                limit: 5,
                offset: (this.pagination.current_page - 1) * this.pagination.per_page,
            };

            return resourceExceptionTimeReport({ data, cancelToken: this.cancelPaginationToken.token })
                .then(({ data }) => {
                    let newData =
                        data.length > 0
                            ? data.filter(ev => ev.actual_call_time > this.callTypesSets.target)
                            : [];
                    this.reportData = newData.map(loc => ({
                        ...loc,
                        actualCallTimeFormatted: secsToReadableTime(loc.actual_call_time),
                        startTimeFormatted: formatDateString(loc.start_time),
                        endTimeFormatted: formatDateString(loc.end_time),
                        details: loc.details.map(event => ({
                            ...event,
                            timeFormatted: formatDateString(event.time),
                        })),
                    }));

                    if (this.reportData.length > 0) {
                        this.cache[this.pagination.current_page] = this.reportData;
                    }
                })
                .catch(response => {
                    // TODO Handler error (https://jira.ascom-ws.com/browse/MERA-1546)
                    return response;
                })
                .then(response => {
                    if (response !== 'canceled') {
                        this.table2Loader = false;
                    }
                });
        },

        /**
         * Finds percent of "total"
         * @param {number} part
         * @param {number} total
         * @returns {number}
         */
        percent(part, total) {
            return Math.floor((part / total) * 100);
        },

        /**
         * Sets text color for detailed tables to $late-color or $delayed-color
         * @param {number} respTime
         * @returns {string} HEX color
         */
        timeColor(respTime) {
            return this.callTypesSets.delayed < respTime ? '#F44236' : '#FEC500';
        },

        resetToDefault() {
            this.weekPerformance = [];
            this.timeStatistics = [];
            this.reportData = [];
            this.exceptionLength = 0;
            this.cache = {};
            this.pagination = {
                current_page: 1,
                last_page: 0,
                per_page: 5,
            };
            this.moreThan26Weeks = false;
            this.table1Loader = true;
            this.table2Loader = true;
            this.chartLoader = true;
        },
    },
};
</script>
